import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { PlaybookModal } from "../modals/PlaybookModal";

interface FAQItemProps {
  question: string;
  answer: string | React.ReactNode;
  defaultOpen?: boolean;
}

const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const answerId = `faq-${question.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-md">
      <Button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        variant="ghost"
        aria-expanded={isOpen}
        aria-controls={answerId}
        className="group flex h-auto min-h-[4.5rem] w-full items-center justify-between rounded-2xl px-4 py-6 text-left transition-all duration-300 hover:bg-gray-50/50 focus:outline-none sm:px-8 sm:py-8"
      >
        <span className="break-words text-sm font-semibold leading-tight text-gray-900 transition-colors duration-300 group-hover:text-blue-600 sm:text-lg sm:leading-normal">
          {question}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-5 w-5 flex-none text-gray-400 transition duration-300 group-hover:text-blue-500 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </Button>
      <div
        id={answerId}
        aria-hidden={!isOpen}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-[32rem] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 pt-2 sm:px-8 sm:pb-6">
          <div className="border-t border-gray-100 pt-4">
            {typeof answer === "string" ? (
              <p className="break-words text-sm leading-relaxed text-gray-600 sm:text-base">
                {answer}
              </p>
            ) : (
              <div className="break-words text-sm leading-relaxed text-gray-600 sm:text-base">
                {answer}
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export const FAQSection: React.FC = () => {
  const [showPlaybook, setShowPlaybook] = useState(false);

  const faqItems = [
    {
      question: "I just bought a subscription. Now what?",
      answer: (
        <>
          <button
            type="button"
            onClick={() => setShowPlaybook(true)}
            className="cursor-pointer border-0 bg-transparent p-0 text-left font-bold text-blue-600 underline hover:text-blue-700 focus-visible:rounded focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            Secure your viral tutorial by clicking here.
          </button>
          {
            " This guide gives you the exact steps to master social media algorithms, helping you go viral and earn thousands of dollars a month. It teaches you the proven formula, showing you exactly how to produce viral content by optimizing key metrics like watch time and retention."
          }
        </>
      ),
    },
    {
      question: "How do I view my usage?",
      answer: "You can view your usage in your Account.",
    },
    {
      question: "Do I own the videos?",
      answer: "Yes, you retain full ownership of all clips created with TopClip.",
    },
    {
      question: "Can I monetize videos created with TopClip?",
      answer: "Yes. You can monetize your clips on TikTok and YouTube.",
    },
    {
      question: "What is a video credit?",
      answer: "One credit is equal to one minute of video processing.",
    },
    {
      question: "Is it possible to cancel my plan?",
      answer:
        "Yes, you can cancel your plan at any time. Your plan will remain active until the end of the billing cycle.",
    },
    {
      question: "Can I switch my plan after signing up?",
      answer:
        "Absolutely. Whether you want to upgrade or downgrade, you can change your plan whenever you would like.",
    },
    {
      question: "Have another question?",
      answer:
        "Feel free to reach out to our support team at hello@topclip.ai and we'll be happy to help!",
    },
  ] as const;

  return (
    <div
      data-faq-accordion
      className="mt-14 space-y-4"
      aria-label="More frequently asked questions"
    >
      <PlaybookModal isOpen={showPlaybook} onClose={() => setShowPlaybook(false)} />
      {faqItems.map((item, index) => (
        <FAQItem
          key={item.question}
          question={item.question}
          answer={item.answer}
          defaultOpen={index === 0}
        />
      ))}
    </div>
  );
};
